/**
 * @file Description.tsx
 * @description
 * A collapsible card component that displays bill descriptions from the data store.
 * The component reads bill metadata (specifically the description field) from the
 * global Zustand store and renders it as HTML content.
 *
 * The description HTML is sanitized using DOMPurify before rendering to prevent
 * XSS (Cross-Site Scripting) attacks. While the bill data comes from controlled
 * sources (static JSON files bundled with the app), sanitization provides defense
 * in depth against potential security vulnerabilities.
 *
 * @author Ben Wilson-Langman - Original author
 * @author Cameron Owens - Added dynamic functionality through store data routing
 */

import React, { useState } from 'react';
import Collapse from 'react-bootstrap/Collapse';
import Button from 'react-bootstrap/Button';
import DOMPurify from 'dompurify';
import { useDataStore } from '@/store';

interface IProps {
  open?: boolean;
  children?: React.ReactNode;
}

const Description: React.FC<IProps> = ({ open, children }) => {
  const [isOpen, setIsOpen] = useState(open ?? false);
  const billMetadata = useDataStore((s) => s.billMetadata);

  const description = billMetadata?.description;
  const billId = useDataStore((s) => s.billId);

  const match = billId.match(/^([a-z]+)(\d+)$/i);

  let category = match ? match[1] : ''; // "hr"
  let number = match ? match[2] : ''; // "5"
  if (category.toLowerCase() === 'hr') {
    category = 'house-bill';
  } else if (category.toLowerCase() === 'hres') {
    category = 'house-resolution';
  }

  const handleFilterOpening = () => {
    setIsOpen((prev) => !prev);
  };

  // Sanitize HTML description using DOMPurify to prevent XSS attacks.
  // DOMPurify removes dangerous elements (script tags, event handlers, etc.)
  // while preserving safe HTML formatting (paragraphs, bold text, etc.).
  const content = description ? (
    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
  ) : children ? (
    children
  ) : (
    <p className="text-muted">No description available.</p>
  );

  return (
    <div className="card" onClick={handleFilterOpening} style={{ cursor: 'pointer' }}>
      <div className="fw-bold border-0 pt-0 pb-1 border-bottom d-flex align-items-center">
        <Button
          type="button"
          variant="link"
          className="p-0 me-2 text-decoration-none"
          onClick={(e) => {
            e.stopPropagation();
            handleFilterOpening();
          }}
          aria-expanded={isOpen}
          aria-controls="description-collapse"
        >
          <span className="fs-6 text-black">{isOpen ? '▲' : '▼'}</span>
        </Button>
        <div className="d-flex gap-2 flex-grow-1 align-items-center">
          <h6 className="fw-bold border-0 pt-2 pb-1 mb-0 h4">BILL DESCRIPTION</h6>
          <a
            target="_blank"
            href={`https://www.congress.gov/bill/118th-congress/${category}/${number}`}
            title="link to bill"
          >
            <i className="bi bi-link" style={{ fontSize: '1.5rem' }}></i>
          </a>
        </div>
      </div>

      <Collapse in={isOpen}>
        <div
          id="description-collapse"
          className="border-bottom"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3">
            <br />
            {content}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default Description;
